/* Luma District product gallery. One instance per [data-ld-gallery]; safe with theme-editor reloads. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(g) {
    if (!g || g.dataset.ldInit === '1') return;
    const viewport = g.querySelector('.ld-gallery__viewport');
    const slides = Array.from(g.querySelectorAll('.ld-gallery__slide'));
    if (!viewport || !slides.length) return;
    g.dataset.ldInit = '1';
    const ac = new AbortController();
    const on = { signal: ac.signal };
    g._ldAbort = ac;

    const thumbs = Array.from(g.querySelectorAll('.ld-gallery__thumb'));
    const thumbRow = g.querySelector('[data-thumbs]');
    const prev = g.querySelector('[data-prev]');
    const next = g.querySelector('[data-next]');
    const counter = g.querySelector('[data-current]');
    let index = Math.min(parseInt(g.dataset.start || '0', 10) || 0, slides.length - 1);

    function setActive(i) {
      index = i;
      thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
      if (counter) counter.textContent = String(i + 1);
      slides.forEach((s, j) => {
        s.setAttribute('aria-hidden', j === i ? 'false' : 'true');
        s.querySelectorAll('button, a, video, iframe').forEach(el => { el.tabIndex = j === i ? 0 : -1; });
        const v = s.querySelector('video');
        if (v && j !== i) v.pause();
      });
      const t = thumbs[i];
      if (t && thumbRow) {
        const left = t.offsetLeft - thumbRow.clientWidth / 2 + t.clientWidth / 2;
        thumbRow.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    let programmatic = false;
    let progTimer;
    function go(i, smooth = true) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      programmatic = true;
      clearTimeout(progTimer);
      progTimer = setTimeout(() => { programmatic = false; }, smooth && !reduceMotion ? 900 : 120);
      viewport.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      setActive(i);
    }
    g._ldGo = go;

    // Swipe / trackpad scrolling keeps arrows, counter and thumbnails in sync.
    let timer;
    viewport.addEventListener('scroll', () => {
      if (programmatic) return; // arrows/thumbs/keys already set the position
      clearTimeout(timer);
      timer = setTimeout(() => {
        const w = viewport.clientWidth || 1;
        const i = Math.round(viewport.scrollLeft / w);
        if (i !== index && i >= 0 && i < slides.length) setActive(i);
      }, 80);
    }, { passive: true, signal: ac.signal });
    viewport.addEventListener('scrollend', () => { programmatic = false; }, { passive: true, signal: ac.signal });
    // A finger on the photo always wins over an in-flight programmatic scroll.
    viewport.addEventListener('pointerdown', () => { programmatic = false; }, { passive: true, signal: ac.signal });
    viewport.addEventListener('touchstart', () => { programmatic = false; }, { passive: true, signal: ac.signal });

    if (prev) prev.addEventListener('click', () => go(index - 1), on);
    if (next) next.addEventListener('click', () => go(index + 1), on);
    thumbs.forEach((t, i) => t.addEventListener('click', () => go(i), on));

    viewport.addEventListener('keydown', e => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
      else if (e.key === 'Home') { e.preventDefault(); go(0); }
      else if (e.key === 'End') { e.preventDefault(); go(slides.length - 1); }
    }, on);

    // Variant changes (event fired by global.js) jump to that variant's photo; all photos stay available.
    document.addEventListener('variant:media', e => {
      const root = g.closest('[data-product]');
      if (!root || !e.detail || e.detail.root !== root) return;
      const i = slides.findIndex(s => s.dataset.mediaId === String(e.detail.mediaId));
      if (i > -1 && i !== index) go(i);
    }, on);

    window.addEventListener('resize', () => {
      viewport.scrollTo({ left: slides[index].offsetLeft, behavior: 'auto' });
    }, { passive: true, signal: ac.signal });

    // ---------- Full-screen viewer ----------
    const dialog = g.querySelector('[data-lightbox]');
    const dataEl = g.querySelector('[data-ld-gallery-images]');
    let images = [];
    try { images = dataEl ? JSON.parse(dataEl.textContent) : []; } catch (err) { images = []; }
    const imageIdx = images.map((m, i) => (m.image ? i : -1)).filter(i => i > -1);
    if (dialog && imageIdx.length && typeof dialog.showModal === 'function') {
      const img = dialog.querySelector('[data-lightbox-img]');
      const lbCount = dialog.querySelector('[data-lightbox-current]');
      const lbPrev = dialog.querySelector('[data-lightbox-prev]');
      const lbNext = dialog.querySelector('[data-lightbox-next]');
      let lb = 0;
      let opener = null;
      const show = pos => {
        pos = Math.max(0, Math.min(imageIdx.length - 1, pos));
        lb = pos;
        const m = images[imageIdx[pos]];
        img.src = m.src;
        img.alt = m.alt || '';
        lbCount.textContent = String(imageIdx[pos] + 1);
        if (lbPrev) lbPrev.disabled = pos === 0;
        if (lbNext) lbNext.disabled = pos === imageIdx.length - 1;
      };
      g.querySelectorAll('[data-zoom]').forEach(btn => btn.addEventListener('click', () => {
        opener = btn;
        const p = imageIdx.indexOf(parseInt(btn.dataset.zoom, 10));
        show(p < 0 ? 0 : p);
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      }, on));
      const close = () => { if (dialog.open) dialog.close(); };
      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        const target = imageIdx[lb];
        if (typeof target === 'number') go(target, false);
        if (opener) opener.focus();
      }, on);
      dialog.querySelector('[data-lightbox-close]').addEventListener('click', close, on);
      if (lbPrev) lbPrev.addEventListener('click', () => show(lb - 1), on);
      if (lbNext) lbNext.addEventListener('click', () => show(lb + 1), on);
      dialog.addEventListener('click', e => { if (e.target === dialog) close(); }, on);
      dialog.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight') { e.preventDefault(); show(lb + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); show(lb - 1); }
      }, on);
      let sx = null;
      img.addEventListener('touchstart', e => { sx = e.touches[0].clientX; }, { passive: true, signal: ac.signal });
      img.addEventListener('touchend', e => {
        if (sx === null) return;
        const dx = e.changedTouches[0].clientX - sx;
        sx = null;
        if (Math.abs(dx) > 40) show(lb + (dx < 0 ? 1 : -1));
      }, { passive: true, signal: ac.signal });
    } else {
      // No dialog support or no images: make the photo buttons inert rather than broken.
      g.querySelectorAll('[data-zoom]').forEach(btn => { btn.style.cursor = 'default'; btn.removeAttribute('aria-haspopup'); });
    }

    requestAnimationFrame(() => go(index, false));
  }

  function initAll(scope) { (scope || document).querySelectorAll('[data-ld-gallery]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', e => initAll(e.target));
  document.addEventListener('shopify:section:unload', e => {
    e.target.querySelectorAll('[data-ld-gallery]').forEach(g => {
      if (g._ldAbort) g._ldAbort.abort();
      delete g.dataset.ldInit;
    });
  });
})();
